import * as React from 'react'
import { DropResult } from 'react-beautiful-dnd'

import {
  ApprovalGroupDetailsFragment,
  ApprovalsRuleConfigurationFragment,
} from 'generated/resources/graphql'

import {
  DragAndDropContext,
  DraggableComponent,
  DropableBox,
} from 'modules/core/components/DragAndDrop'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { RuleCard } from 'modules/approvals/components/RuleCard'
import { useSortRulesConfigurations } from 'modules/approvals/hooks/useRulesConfigurations'

interface ListProps {
  group: ApprovalGroupDetailsFragment
}

export const List = ({ group }: ListProps) => {
  const { sortRulesConfigurations } = useSortRulesConfigurations()
  const [sortedRules, setSortedRules] = React.useState(group.rulesConfiguration || [])

  const onDragEnd = ({ draggableId, destination }: DropResult) => {
    const id = parseInt(draggableId.match(/\d+/)?.[0] || '0')
    if (!id || !destination) return

    const movedRule = sortedRules.find((rule) => rule.id === id)
    if (!movedRule) return
    const otherRules = sortedRules.filter((rule) => rule.id !== id)
    otherRules.splice(destination.index, 0, movedRule)
    setSortedRules(otherRules)
    const data = otherRules.map((rule, index) => ({ id: rule.id, position: index }))
    sortRulesConfigurations(data)
  }

  useUnsafeEffect(() => {
    // We need to sort when some rules are deleted or added
    if (group.rulesConfiguration.length !== sortedRules.length) {
      setSortedRules(group.rulesConfiguration || [])
    }
  }, [group.rulesConfiguration, sortedRules.length])

  const ruleCard = (
    rule: ApprovalsRuleConfigurationFragment,
    index: number,
    draggable: boolean
  ) => {
    return (
      <RuleCard
        group={group}
        index={index + 1}
        rule={rule}
        key={rule.id}
        flow={rule.flow}
        title={rule.name}
        description={rule.description}
        decision={rule.onRulesMet}
        dragabble={draggable}
      />
    )
  }

  if (group.domainConfigurationRules.length < 2) {
    return <>{sortedRules.map((rule, index) => ruleCard(rule, index, false))}</>
  }

  return (
    <DragAndDropContext onDragEnd={onDragEnd}>
      <DropableBox droppableId="rules" type="rule">
        {sortedRules.map((rule, index) => (
          <DraggableComponent key={rule.id} index={index} id={rule.id?.toString() ?? ''}>
            {ruleCard(rule, index, true)}
          </DraggableComponent>
        ))}
      </DropableBox>
    </DragAndDropContext>
  )
}
